import React from 'react';
import { Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { ActivityType } from 'gql';
import { CountStatsSection } from 'modules/home/components/CountStatsSection/CountStatsSection';
import { activityTypePluralOrSigleMessages } from 'modules/home/messages';
import { calculateActivitiesCountDelta } from 'modules/home/utils';
import { DeltaIcon } from 'modules/home/components/CountStatsSection/DeltaIcon';

interface ActivitiesSectionProps {
  isLoading: boolean
  totalActivities: number
  activitiesByType: { activityType: ActivityType, count: number }[]
  activitiesByTheme: { themeId: number, themeName: string, count: number }[]
  previousActivitiesByType?: { activityType: ActivityType, count: number }[]
  previousActivitiesByTheme?: { themeId: number, themeName: string, count: number }[]
  renderEmptyState: () => React.ReactNode
}

export const ActivitiesSection: React.FC<ActivitiesSectionProps> = ({
  isLoading,
  totalActivities,
  activitiesByType,
  activitiesByTheme,
  previousActivitiesByType,
  previousActivitiesByTheme,
  renderEmptyState
}) => {
  const { formatMessage } = useIntl();
  const getPreviousTypeCount = (activityType: ActivityType) =>
    previousActivitiesByType?.find(item => item.activityType === activityType)?.count;
  const getPreviousThemeCount = (themeId: number) =>
    previousActivitiesByTheme?.find(item => item.themeId === themeId)?.count;

  const renderDeltaCell = (current: number, previous?: number) => {
    const delta = previous != undefined ? calculateActivitiesCountDelta(current, previous) : undefined;
    if (delta == undefined) {
      return <Typography variant='body2'>-</Typography>;
    }

    return (
      <Stack direction='row' justifyContent='flex-end' alignItems='center' gap={0.5}>
        <Typography variant='body2'>{`${delta}%`}</Typography>
        <DeltaIcon currentValue={current} previousValue={previous ?? 0} />
      </Stack>
    );
  };

  return (
    <CountStatsSection
      title={formatMessage({ id: 'Activities' })}
      totalCount={totalActivities}
      isLoading={isLoading}
    >
      <Paper sx={{ p: 2, flex: '1 1 100%' }}>
        <Typography variant='h6' gutterBottom>{formatMessage({ id: 'Activities by type' })}</Typography>
        {activitiesByType.length
          ? (
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>{formatMessage({ id: 'Type' })}</TableCell>
                  <TableCell align='right'>{formatMessage({ id: 'Activities' })}</TableCell>
                  <TableCell align='right'>{formatMessage({ id: 'Delta' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activitiesByType.map(item => (
                  <TableRow key={item.activityType}>
                    <TableCell>{formatMessage(activityTypePluralOrSigleMessages[item.activityType], { activityCount: item.count })}</TableCell>
                    <TableCell align='right'>{item.count}</TableCell>
                    <TableCell align='right'>{renderDeltaCell(item.count, getPreviousTypeCount(item.activityType))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
          : renderEmptyState()
        }
      </Paper>

      <Paper sx={{ p: 2, flex: '1 1 100%' }}>
        <Typography variant='h6' gutterBottom>{formatMessage({ id: 'Activities by theme' })}</Typography>
        {activitiesByTheme.length
          ? (
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>{formatMessage({ id: 'Theme' })}</TableCell>
                  <TableCell align='right'>{formatMessage({ id: 'Activities' })}</TableCell>
                  <TableCell align='right'>{formatMessage({ id: 'Delta' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activitiesByTheme.map(item => (
                  <TableRow key={item.themeId}>
                    <TableCell>{item.themeName}</TableCell>
                    <TableCell align='right'>{item.count}</TableCell>
                    <TableCell align='right'>{renderDeltaCell(item.count, getPreviousThemeCount(item.themeId))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
          : renderEmptyState()
        }
      </Paper>
    </CountStatsSection>
  );
};
